import Typography from "@portal/components/atoms/v2/typography";
import { FC, useMemo, useState } from "react";
import { formatNumberTwoDigits } from "@portal/utils/numberFormatters";
import { COLORS, REGIONS } from "./mapChart/helper";
import { IFundMorningstarRegionsProps } from "./types";
import { TextResources } from "@portal/assets/text/TextResources";
import RegionChart from "./mapChart";

const FundMorningstarRegions: FC<IFundMorningstarRegionsProps> = ({ morningstarSecurities }) => {
  const [showXRegions, setShowXRegions] = useState(5);

  const regions = useMemo(() => {
    return morningstarSecurities?.portfolios?.[0].regionalExposure
      ?.find(cp => cp.salePosition === "N")
      ?.breakdownValues.filter(r => r.value > 0 && REGIONS.find(region => region.type === r.type))
      .sort((a, b) => b.value - a.value)
      .map((r, index) => ({
        ...REGIONS.find(region => region.type === r.type),
        value: r.value,
        color: COLORS[index % COLORS.length],
      })) as typeof REGIONS;
  }, [morningstarSecurities]);

  const highestRegionValue = regions?.[0].value;

  if (!regions || regions.length === 0) {
    return (
      <div className="flex justify-center">
        <Typography>{TextResources.FUND_DISTRIBUTION_NO_REGION_DATA}</Typography>
      </div>
    );
  }

  return (
    <ul className="px-2 mt-4 flex flex-col gap-2">
      <RegionChart regions={regions} />
      {regions?.slice(0, showXRegions).map(region => {
        if (!region.value) return null;
        const width = highestRegionValue ? Math.round((region.value / (highestRegionValue ?? 1)) * 100) : 0;
        return (
          <li key={region.type} className="flex justify-between items-center gap-2">
            <div className="w-full">
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: region.color }}></span>
                <Typography>{region.name}</Typography>
              </div>
              <div className="bg-blueLight rounded-full h-0.5" style={{ width: `${width}%` }}></div>
            </div>
            <div className="justify-self-end">
              <Typography>{formatNumberTwoDigits(region.value)}%</Typography>
            </div>
          </li>
        );
      })}
      {regions && regions.length > 5 && (
        <li className="flex justify-center mt-2">
          <button
            className="underline hover:text-blueHighlighted"
            onClick={() => setShowXRegions(prev => (prev === 5 ? regions.length : 5))}
          >
            {showXRegions === 5 ? "Vis alle regioner" : "Vis færre regioner"}
          </button>
        </li>
      )}
    </ul>
  );
};

export default FundMorningstarRegions;
